import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import { makeStyles } from '@material-ui/core/styles';
import {isComponentVisibleForUser, isComponentEnabledForUser} from 'utils';
import { CustomCircleChart, CustomLineChart } from 'components/elements';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import FavoriteIcon from '@material-ui/icons/Favorite';

const useStyles = makeStyles((theme) => ({
  mr: {
    marginRight: theme.spacing(1),
  },
  mb: {
    paddingLeft: '10px', paddingRigth: '10px', paddingTop: '10px',
    marginBottom: '20px',
    display: 'flex', flexDirection: 'row', 
  },
  zoneCard: {
    height: 645,
    position: 'relative',
    backgroundColor: 'white',
    marginTop: theme.spacing(2),
    borderRadius: '10px'
  },
  zoneTitle: {
    fontSize: 20,
    color: theme.palette.base[400],
  },
  editIcon: {
    color: theme.palette.base[400],
    marginRight: 'auto'
  },
  zoneCardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(0.5),
  },
  circleChart: {
    fontWeight: 400,


    '& .apexcharts-datalabel-label': {
      fontWeight: 'normal',
    },
  },
  graphWrapper: {
    width: `calc(100% - 20px)`,
    height: 150,
    '& img': {
      width: '100%',
      height: '100%',
    },
    backgroundColor: theme.palette.background.default,
    borderRadius: '10px',
    margin: '10px'
  },
  zoneStatus: {
    right: theme.spacing(3),
    top: theme.spacing(2),
  },
  zoneStatusLabel: {
    fontSize: 14,
    opacity: 0.5
  },
  zoneStatusValue: {
    fontSize: 50,
    marginLeft: 'auto',
    marginTop: '15px',
    paddingRight: '10px'
  },
  error: {
    color: theme.palette.primary.red,
    fontSize: 20,
  },
  container: {
    backgroundColor: theme.palette.background.default,
    borderRadius: '10px',
    width:'50%', margin: '10px'
  },
  line: {
    border: '1px solid #dcdcdc', marginLeft: '10px', marginRight: '10px', marginTop: '10px'
  },
  firstRow: {
    paddingLeft:'20px', paddingTop: '20px', opacity: 0.6, display: 'flex', flexDirection: 'row'
  }

}));

function ZoneCard(props) {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    zone,
    cardWidth,
    histories,
    handleEditZoneOpen,
    userUseCases,
    handleZoneFavorite,
    isZoneFavorite
  } = props;

  const handleEditZone = (zone) => () => {
    handleEditZoneOpen(zone);
  };

  const occupationHistories = histories;
  const categories = [];
  const chartData = [];

  occupationHistories.sort((a, b) => {
    const dateA = a.dateTS;
    const dateB = b.dateTS;

    if (dateA < dateB) {
      return -1;
    }

    return (dateA > dateB) ? 1 : 0;
  });

  const getHour = (history) => parseInt(history.date.slice(11, 13), 10);

  const lastHour = occupationHistories.length > 0
    ? getHour(occupationHistories[occupationHistories.length - 1])
    : 0;

  for (let i = 0; i < 24; i++) {
    const historyByHour = occupationHistories.find((history) => getHour(history) === i);
    categories.push(i);
    if (occupationHistories!==null && occupationHistories.length > 0 && historyByHour) {
      /* eslint-disable-next-line no-mixed-operators */
      chartData.push(Math.floor(historyByHour.occupancy * 100 / historyByHour.capacity));
    } else {
      chartData.push(i + 1 < lastHour ? 0 : -100000);
    }
  }

  const isReservedValid = zone.capacity >= zone.reserved; // zone.reserved >= 0 &&
  const isFreeReservedValid = zone.freeReserved <= zone.reserved; // zone.freeReserved >= 0 &&
  const isFreeOthersValid = zone.freeOthers <= zone.capacity - zone.reserved; // zone.freeOthers >= 0 &&

  return (
    <div
      className={classes.zoneCard}
      style={{ width: cardWidth }}
    >
      <div className={classes.zoneCardHeader}>
        <div className={classes.zoneTitle}>
          {zone.zoneName || t('NO_NAME')}
        </div>
        {isComponentVisibleForUser(UC_CARD_ZONE_EDIT, userUseCases) &&
          <IconButton 
            style={{marginLeft: 'auto'}}
            edge="start"
            color="inherit"
            aria-label="open edit"
            disabled={!isComponentEnabledForUser(UC_CARD_ZONE_EDIT, userUseCases)}
            onClick={handleEditZone(zone)}            
          >
              <EditIcon className={classes.editIcon}/>
          </IconButton>
        }
      </div>
      <div style={{display: 'flex', direction: 'row'}}>
        <div className={classnames(classes.circleChart, classes.container)}>
          <div className={classes.firstRow}>
            <div>{t('OCCUPANCY').toUpperCase()}</div>
            <div style={{marginLeft: 'auto', paddingRight: '20px', cursor: 'pointer'}}>
              {!isZoneFavorite(zone.zoneId, TYPE_FT_ZONE_BAR) && 
                <FavoriteBorderIcon onClick={()=>{handleZoneFavorite(zone.zoneId, !isZoneFavorite(zone.zoneId, TYPE_FT_ZONE_BAR), TYPE_FT_ZONE_BAR)}}/>
              }
              {isZoneFavorite(zone.zoneId, TYPE_FT_ZONE_BAR) && 
                <FavoriteIcon onClick={()=>{handleZoneFavorite(zone.zoneId, !isZoneFavorite(zone.zoneId, TYPE_FT_ZONE_BAR), TYPE_FT_ZONE_BAR)}}/>
              }
            </div>
          </div>
          <CustomCircleChart
            height={250}
            series={[zone.progress!==null?(zone.progress>100?100:zone.progress):'-']}
            label=''
          />
        </div>

        <div className={classnames(classes.zoneStatus, classes.container)}>

          <div className={classes.firstRow}>
            <div style={{marginLeft: 'auto', paddingRight: '20px', cursor: 'pointer'}}>
              {!isZoneFavorite(zone.zoneId, TYPE_FT_ZONE_INFO) && 
                <FavoriteBorderIcon onClick={()=>{handleZoneFavorite(zone.zoneId, !isZoneFavorite(zone.zoneId, TYPE_FT_ZONE_INFO), TYPE_FT_ZONE_INFO)}}/>
              }
              {isZoneFavorite(zone.zoneId, TYPE_FT_ZONE_INFO) && 
                <FavoriteIcon onClick={()=>{handleZoneFavorite(zone.zoneId, !isZoneFavorite(zone.zoneId, TYPE_FT_ZONE_INFO), TYPE_FT_ZONE_INFO)}}/>
              }
            </div>
          </div>

          <div className={classes.mb}>
            <div className={classes.zoneStatusLabel}>{t('CAPACITY').toUpperCase()}</div>
            <div className={classnames(classes.zoneStatusValue, !zone.validData && classes.error)}>
              {zone.validData ? (zone.capacity!==null?zone.capacity:'-') : t('INVALID_DATA')}
            </div>
          </div>

          <div className={classes.line}></div>

          <div className={classes.mb} >
            <div className={classes.zoneStatusLabel}>{t('RESERVED').toUpperCase()}</div>
            <div className={classnames(classes.zoneStatusValue, !isReservedValid && classes.error)}>
              {isReservedValid ? (zone.reserved!==null?zone.reserved:'-') : t('INVALID_DATA')}
            </div>
          </div>

          <div className={classes.line}></div>

          <div className={classes.mb}>
            <div className={classes.zoneStatusLabel}>{t('FREE_RESERVED').toUpperCase()}</div>
            <div className={classnames(classes.zoneStatusValue, !isFreeReservedValid && classes.error)} >
              {isFreeReservedValid ? (zone.freeReserved!==null?zone.freeReserved:'-') : t('INVALID_DATA')}
            </div>
          </div>

          <div className={classes.line}></div>

          <div className={classes.mb}>
            <div className={classes.zoneStatusLabel}>{t('OTHERS').toUpperCase()}</div>
            <div className={classnames(classes.zoneStatusValue, !isReservedValid && classes.error)}>
              {isReservedValid ? (zone.others!==null?zone.others:'-') : t('INVALID_DATA')}
            </div>
          </div>

          <div className={classes.line}></div>

          <div className={classes.mb}>
            <div className={classes.zoneStatusLabel}>{t('FREE_OTHERS').toUpperCase()}</div>
            <div className={classnames(classes.zoneStatusValue, !isFreeOthersValid && classes.error)}>
              {isFreeOthersValid ? (zone.freeOthers!==null?zone.freeOthers:'-') : t('INVALID_DATA')}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.graphWrapper}>
        <CustomLineChart
          categories={categories}
          data={chartData}
          name={zone.zoneName || t('NO_NAME')}
        />
      </div>
    </div>
  );
}

ZoneCard.propTypes = {
  zone: PropTypes.object.isRequired,
  cardWidth: PropTypes.string.isRequired,
  histories: PropTypes.array.isRequired,
  handleEditZoneOpen: PropTypes.func.isRequired,
};

const UC_CARD_ZONE_EDIT = 'UC0019';
const TYPE_FT_ZONE_BAR = 'FT_ZONE_BAR';
const TYPE_FT_ZONE_INFO = 'FT_ZONE_INFO';


export default ZoneCard;
